import React, { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { FormField } from '../form/FormField.tsx';

export interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  label?: string;
  error?: string;
  required?: boolean;
  hint?: string;
}

const inputClassName = (error?: string) =>
  `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 ${
    error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
  }`;

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ label, error, required, hint, id, placeholder = '••••••••', disabled, readOnly, ...rest }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const canToggle = !disabled && !readOnly;

    return (
      <FormField label={label} error={error} required={required} hint={hint} htmlFor={id}>
        <div className="relative">
          <input
            ref={ref}
            id={id}
            type={showPassword ? 'text' : 'password'}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            className={`${inputClassName(error)} pr-10`}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            {...rest}
          />
          <button
            type="button"
            onClick={() => canToggle && setShowPassword((value) => !value)}
            disabled={!canToggle}
            className={`absolute right-3 top-1/2 -translate-y-1/2 ${
              canToggle ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed'
            }`}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </FormField>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';
